import { DirectionalHint, IButtonProps as FluentUIButtonProps, IContextualMenuItem } from '@fluentui/react';
import {
  Button,
  buttonStylesDefaultWidth,
  buttonStylesPrimary,
  buttonStylesSplit,
  buttonStylesSplitPrimary,
  type IButtonProps,
  IconName,
  loaderStylesSpinnerButtonPrimary,
} from '@h2oai/ui-kit';

import {
  type App,
  type AppInstance,
  AppInstance_Status,
  AppInstance_Visibility,
  type LaunchProfiles,
} from '../../../ai.h2o.cloud.appstore';
import { InstancePauseResumeOpEnum } from '../../../utils/utils';

interface GetActionButtonParams {
  app: App;
  mainInstance: AppInstance | null;
  isAdmin: boolean;
  isOwner: boolean;
  isManagedApp: boolean;
  launchProfiles: LaunchProfiles[] | undefined;
  publicMode: boolean;
  isInstancesLoading: boolean;
  appLocation: string | null;
  handleRunApp: (visibility?: AppInstance_Visibility, profile?: string, runAsAdmin?: boolean) => Promise<void>;
  runApp: (
    visibility: AppInstance_Visibility,
    profile?: string,
    instanceId?: string,
    runAsAdmin?: boolean
  ) => Promise<void>;
  setSuspension: (instance: AppInstance, op: InstancePauseResumeOpEnum) => () => void;
  setHiddenAdvancedRunPanel: (hidden: boolean) => void;
}

export const getActionButton = (params: GetActionButtonParams) => {
  const {
    app,
    isAdmin,
    isOwner,
    isManagedApp,
    publicMode,
    appLocation,
    mainInstance,
    launchProfiles,
    isInstancesLoading,
    runApp,
    handleRunApp,
    setSuspension,
    setHiddenAdvancedRunPanel,
  } = params;

  return (loadingState?: [string, boolean]) => {
    const loading = loadingState?.[1] || !app.id || false;

    // Ownership and managed app checks
    // Owners can always run. Non-owners can run only if the app is not managed.
    const canRun = isOwner || !isManagedApp;
    const isAdminViewingOthersManagedApp = !isOwner && isAdmin && isManagedApp;

    const visitApp = () => {
      if (appLocation) window.open(appLocation, '_blank');
    };

    const loaderProps = {
      label: 'Loading...',
      styles: loaderStylesSpinnerButtonPrimary,
    };

    const runButton: IButtonProps = {
      text: 'Run',
      iconName: IconName.MSNVideos,
      disabled: !canRun,
      loading,
      loaderProps,
      onClick: () => {
        void handleRunApp();
      },
      'data-test': 'public-run-action-button',
    };

    const adminRunButton: IButtonProps = {
      text: 'Run as administrator',
      iconName: IconName.Shield,
      disabled: false,
      loading,
      loaderProps,
      onClick: () => {
        void handleRunApp(undefined, undefined, true);
      },
      'data-test': 'admin-run-action-button',
      styles: loading ? {} : { root: { width: 200 } },
    };

    const runPrivateButton: IButtonProps = {
      text: 'Run In Private',
      iconName: IconName.Shield,
      disabled: !canRun,
      onClick: () => {
        void runApp(AppInstance_Visibility.PRIVATE);
      },
      'data-test': 'private-run-action-button',
      loading,
      loaderProps,
    };

    const visitButton: IButtonProps = {
      text: 'Visit',
      iconName: IconName.ArrowUpRight,
      disabled: false,
      onClick: visitApp,
      'data-test': 'visit-action-button',
      loading,
      loaderProps,
    };

    const visitPublicButton: IButtonProps = {
      text: 'Visit',
      iconName: IconName.ArrowUpRight,
      disabled: !appLocation,
      onClick: visitApp,
      'data-test': 'visit-action-button',
      loading,
      loaderProps,
    };

    const resumeButton: IButtonProps = {
      text: 'Resume',
      iconName: IconName.MSNVideos,
      disabled: false,
      onClick: setSuspension(mainInstance || ({} as AppInstance), InstancePauseResumeOpEnum.Resume),
      'data-test': 'resume-action-button',
      loading,
      loaderProps,
    };

    const notAvailableButton: IButtonProps = {
      text: 'Not available',
      disabled: true,
      loading: false,
      loaderProps,
      onClick: () => {},
      'data-test': 'cannot-run-app-action-button',
    };

    const runWithProfileButton: IButtonProps = {
      text: 'Run with Profile',
      iconName: IconName.MSNVideos,
      disabled: false,
      onClick: () => {
        setHiddenAdvancedRunPanel(false);
      },
      'data-test': 'run-profile-action-button',
      loading,
      loaderProps,
    };

    // Helper function to convert button props to menu items
    const toMenuItem = (button: IButtonProps, key: string): IContextualMenuItem => ({
      key,
      text: button.text,
      iconProps: button.iconName ? { iconName: button.iconName } : undefined,
      disabled: button.disabled,
      onClick: (ev) => {
        if (button.onClick) button.onClick(ev as any);
      },
    });

    // Build dropdown buttons array - Run as Administrator always first (when present)
    const getDropdownButtons = (includeAdminButton: boolean, disableRunInPrivate = false) => [
      ...(includeAdminButton && isAdmin ? [adminRunButton] : []),
      disableRunInPrivate ? { ...runPrivateButton, disabled: true } : runPrivateButton,
      ...(launchProfiles ? [runWithProfileButton] : []),
    ];

    let primaryButton: IButtonProps;
    const menuItems: IContextualMenuItem[] = [];

    // Case 1: Public Mode - Visit button only (no dropdown)
    // No menu items for public mode
    if (publicMode) {
      primaryButton = visitPublicButton;
    }
    // Case 2: Visitable Instance - Visit button with conditional dropdown
    else if (!!appLocation) {
      primaryButton = visitButton;
      // Add menu items if user can run the app or is a platform admin viewing someone else's managed app
      if (app.runnable && (canRun || isAdminViewingOthersManagedApp)) {
        if (isAdminViewingOthersManagedApp) {
          // Case 2f: Non-Owner, Admin, Managed - Visit + dropdown with disabled Run and Run In Private
          const case2fButtons = getDropdownButtons(true, true);
          menuItems.push(
            toMenuItem({ ...runButton, disabled: true }, 'run-app'),
            ...case2fButtons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown'))
          );
        } else {
          // Case 2a-2e: Other visitable cases - Visit + dropdown with Run
          const dropdownButtons = getDropdownButtons(true, false);
          menuItems.push(
            toMenuItem(runButton, 'run-app'),
            ...dropdownButtons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown'))
          );
        }
      }
      // Case 2g-2h: Non-Owner, Non-Admin - Visit button only (no dropdown)
    }
    // Case 3: Admin Run Primary - Run as administrator button with dropdown
    else if (app.runnable && isAdminViewingOthersManagedApp) {
      primaryButton = adminRunButton;
      // Don't include adminRunButton in dropdown since it's primary
      const case3Buttons = getDropdownButtons(false, true);
      menuItems.push(
        toMenuItem({ ...runButton, disabled: true }, 'run-app'),
        ...case3Buttons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown'))
      );
    }
    // Case 4: Pending Instance - Run button with dropdown
    else if (app.runnable && mainInstance?.status === AppInstance_Status.PENDING) {
      primaryButton = { ...runButton, disabled: true }; // Disabled for pending instance
      // Include adminRunButton in dropdown if admin
      const dropdownButtons = getDropdownButtons(true, false);
      menuItems.push(...dropdownButtons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown')));
    }
    // Case 5: Suspended Instance - Resume button with dropdown
    else if (app.runnable && mainInstance) {
      primaryButton = resumeButton;
      // Only add dropdown if user can run the app
      if (canRun || isAdminViewingOthersManagedApp) {
        const dropdownButtons = getDropdownButtons(true, false);
        menuItems.push(...dropdownButtons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown')));
      }
    }
    // Case 6: Runnable App (no instance) - Run button with dropdown
    else if (app.runnable) {
      primaryButton = runButton;
      // Only add dropdown if user can run the app
      if (canRun || isAdminViewingOthersManagedApp) {
        const dropdownButtons = getDropdownButtons(true, false);
        menuItems.push(...dropdownButtons.map((btn) => toMenuItem(btn, btn['data-test'] || 'unknown')));
      }
    }
    // Default: Not Available - No action available
    // No menu items for non-runnable apps
    else {
      primaryButton = notAvailableButton;
    }

    const hasMenu = menuItems?.length > 0 && !publicMode;

    const baseStyles = hasMenu
      ? [buttonStylesSplit, buttonStylesSplitPrimary]
      : [buttonStylesPrimary, buttonStylesDefaultWidth];
    const mergedStyles = primaryButton.styles
      ? [...baseStyles, ...(Array.isArray(primaryButton.styles) ? primaryButton.styles : [primaryButton.styles])]
      : baseStyles;

    return (
      <Button
        {...primaryButton}
        disabled={publicMode ? primaryButton.disabled : isInstancesLoading || primaryButton.disabled || loading}
        loading={publicMode ? primaryButton.loading : isInstancesLoading || loading}
        menuItems={hasMenu ? menuItems : undefined}
        styles={mergedStyles}
        split={hasMenu}
        menuIconName={hasMenu ? IconName.ChevronDown : undefined}
        menuDirectionalHint={hasMenu ? DirectionalHint.bottomRightEdge : undefined}
        splitButtonMenuProps={hasMenu ? ({ 'data-test': 'more-actions-button' } as FluentUIButtonProps) : undefined}
        className="app-details-action-button"
      />
    );
  };
};
